import { useState, useEffect, useMemo } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { ArrowUpRight, ArrowDownRight, Minus, GitCompare, ChevronDown, Filter, Download } from 'lucide-react';

const fmt = (n) => {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 4 }).format(Number(n));
};
const fmtCur = (n) => n != null ? `₹${fmt(n)}` : '—';

const STATUS_STYLE = {
  changed: 'bg-amber-50 text-amber-700 border border-amber-200',
  new: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  removed: 'bg-red-50 text-red-700 border border-red-200',
  unchanged: 'bg-slate-100 text-slate-500 border border-slate-200',
};

function DeltaCell({ delta, pct }) {
  if (delta == null) return <span className="text-slate-400">—</span>;
  if (delta > 0) return (
    <span className="flex items-center justify-end gap-0.5 text-red-600 font-mono text-xs font-semibold">
      <ArrowUpRight size={12} />+{fmt(delta)}
      {pct != null && <span className="text-[10px] opacity-70 ml-0.5">({pct}%)</span>}
    </span>
  );
  if (delta < 0) return (
    <span className="flex items-center justify-end gap-0.5 text-emerald-600 font-mono text-xs font-semibold">
      <ArrowDownRight size={12} />{fmt(delta)}
      {pct != null && <span className="text-[10px] opacity-70 ml-0.5">({pct}%)</span>}
    </span>
  );
  return <span className="flex items-center justify-end gap-0.5 text-slate-400 font-mono text-xs"><Minus size={12} />0</span>;
}

export default function RevisionCompare() {
  const [revisions, setRevisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [revOld, setRevOld] = useState('');
  const [revNew, setRevNew] = useState('');
  const [compareData, setCompareData] = useState(null);
  const [comparing, setComparing] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    api.get('/upload/revisions')
      .then(r => {
        setRevisions(r.data);
        setLoading(false);
      })
      .catch(() => { toast.error('Failed to load revisions'); setLoading(false); });
  }, []);

  // Derive unique customers from revisions
  const customers = useMemo(() => {
    const set = new Set();
    revisions.forEach(r => r.customers?.forEach(c => set.add(c)));
    return Array.from(set).sort();
  }, [revisions]);

  // Get available revisions for selected customer
  const customerRevisions = useMemo(() => {
    if (!selectedCustomer) return [];
    return revisions
      .filter(r => r.customers?.includes(selectedCustomer))
      .map(r => r.label);
  }, [revisions, selectedCustomer]);

  // Auto-select revisions when customer changes
  useEffect(() => {
    if (customerRevisions.length >= 2) {
      setRevOld(customerRevisions[1]);
      setRevNew(customerRevisions[0]);
    } else {
      setRevOld('');
      setRevNew('');
    }
    setCompareData(null);
  }, [customerRevisions]);

  const handleCompare = async () => {
    if (!selectedCustomer || !revOld || !revNew) {
      toast.error('Select a customer and two revisions');
      return;
    }
    if (revOld === revNew) {
      toast.error('Select two different revisions');
      return;
    }
    setComparing(true);
    try {
      const res = await api.get(`/upload/revision-compare?customer=${encodeURIComponent(selectedCustomer)}&rev_old=${encodeURIComponent(revOld)}&rev_new=${encodeURIComponent(revNew)}`);
      setCompareData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Comparison failed');
    } finally {
      setComparing(false);
    }
  };

  const filteredItems = useMemo(() => {
    if (!compareData?.items) return [];
    if (statusFilter === 'all') return compareData.items;
    return compareData.items.filter(i => i.status === statusFilter);
  }, [compareData, statusFilter]);

  const handleExportCSV = () => {
    if (!compareData?.items?.length) return;
    const headers = ['Part Number', 'Part Name', 'Grade', `${compareData.rev_old} Price`, `${compareData.rev_new} Price`, 'Delta', 'Delta %', `${compareData.rev_old} RM`, `${compareData.rev_new} RM`, 'Status'];
    const rows = compareData.items.map(i => [
      i.part_number, `"${i.part_name}"`, i.material_grade,
      i.old_price ?? '', i.new_price ?? '', i.price_delta ?? '', i.price_delta_pct ?? '',
      i.old_rm_rate ?? '', i.new_rm_rate ?? '', i.status,
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `compare_${selectedCustomer}_${revOld}_vs_${revNew}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="space-y-4 max-w-6xl">
        <div className="h-10 bg-slate-200 rounded-sm animate-pulse w-64" />
        <div className="h-24 bg-slate-200 rounded-sm animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-6xl" data-testid="revision-compare-page">
      {/* Header */}
      <div>
        <h2 className="font-heading font-bold text-slate-900 text-xl flex items-center gap-2">
          <GitCompare size={20} className="text-orange-500" />
          Revision Comparison
        </h2>
        <p className="text-slate-500 text-sm mt-0.5">
          Compare part pricing across two revisions for a specific customer
        </p>
      </div>

      {/* Selector Row */}
      <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-5" data-testid="compare-selectors">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          {/* Customer */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Customer</label>
            <div className="relative">
              <select
                data-testid="select-customer"
                value={selectedCustomer}
                onChange={e => setSelectedCustomer(e.target.value)}
                className="w-full appearance-none bg-slate-50 border border-slate-300 rounded-sm px-3 py-2 text-sm text-slate-800 font-medium focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 pr-8"
              >
                <option value="">Select customer...</option>
                {customers.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
          </div>

          {/* Old Revision */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Old Revision</label>
            <div className="relative">
              <select
                data-testid="select-rev-old"
                value={revOld}
                onChange={e => setRevOld(e.target.value)}
                disabled={!selectedCustomer}
                className="w-full appearance-none bg-slate-50 border border-slate-300 rounded-sm px-3 py-2 text-sm text-slate-800 font-medium focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 pr-8 disabled:opacity-50"
              >
                <option value="">Select...</option>
                {customerRevisions.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
          </div>

          {/* New Revision */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">New Revision</label>
            <div className="relative">
              <select
                data-testid="select-rev-new"
                value={revNew}
                onChange={e => setRevNew(e.target.value)}
                disabled={!selectedCustomer}
                className="w-full appearance-none bg-slate-50 border border-slate-300 rounded-sm px-3 py-2 text-sm text-slate-800 font-medium focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 pr-8 disabled:opacity-50"
              >
                <option value="">Select...</option>
                {customerRevisions.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
          </div>

          {/* Compare Button */}
          <button
            data-testid="compare-btn"
            onClick={handleCompare}
            disabled={!selectedCustomer || !revOld || !revNew || revOld === revNew || comparing}
            className="flex items-center justify-center gap-2 px-5 py-2 bg-slate-900 text-white text-sm font-bold uppercase rounded-sm hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {comparing ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <GitCompare size={14} />
            )}
            Compare
          </button>
        </div>
      </div>

      {/* Results */}
      {compareData && (
        <>
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="compare-summary">
            <div className="bg-white border border-slate-200 rounded-sm p-4 text-center shadow-sm">
              <p className="font-mono text-2xl font-bold text-slate-900">{compareData.total_compared}</p>
              <p className="text-xs text-slate-500 mt-0.5">Total Parts</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 text-center">
              <p className="font-mono text-2xl font-bold text-amber-700">{compareData.changed}</p>
              <p className="text-xs text-amber-600 mt-0.5">Changed</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4 text-center">
              <p className="font-mono text-2xl font-bold text-emerald-700">{compareData.new_parts}</p>
              <p className="text-xs text-emerald-600 mt-0.5">New</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-sm p-4 text-center">
              <p className="font-mono text-2xl font-bold text-red-700">{compareData.removed_parts}</p>
              <p className="text-xs text-red-600 mt-0.5">Removed</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-sm p-4 text-center shadow-sm">
              <p className="font-mono text-2xl font-bold text-slate-500">{compareData.total_compared - compareData.changed - compareData.new_parts - compareData.removed_parts}</p>
              <p className="text-xs text-slate-500 mt-0.5">Unchanged</p>
            </div>
          </div>

          {/* Filter + Export bar */}
          <div className="flex items-center justify-between">
            <div className="flex gap-1 bg-white border border-slate-200 rounded-sm p-1" data-testid="status-filter">
              {[
                { key: 'all', label: 'All', count: compareData.items.length },
                { key: 'changed', label: 'Changed', count: compareData.changed },
                { key: 'new', label: 'New', count: compareData.new_parts },
                { key: 'removed', label: 'Removed', count: compareData.removed_parts },
                { key: 'unchanged', label: 'Unchanged', count: compareData.total_compared - compareData.changed - compareData.new_parts - compareData.removed_parts },
              ].map(f => (
                <button
                  key={f.key}
                  data-testid={`filter-${f.key}`}
                  onClick={() => setStatusFilter(f.key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-sm transition-colors ${
                    statusFilter === f.key ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {f.label}
                  <span className={`font-mono px-1 py-0.5 rounded-full text-[10px] ${statusFilter === f.key ? 'bg-white/20' : 'bg-slate-100'}`}>{f.count}</span>
                </button>
              ))}
            </div>
            <button
              data-testid="export-csv-btn"
              onClick={handleExportCSV}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-300 rounded-sm text-slate-700 text-xs font-semibold hover:bg-slate-50 transition-colors"
            >
              <Download size={12} /> Export CSV
            </button>
          </div>

          {/* Comparison Table */}
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden" data-testid="compare-table">
            <div className="overflow-auto max-h-[70vh]">
              <table className="w-full text-sm border-collapse sticky-head">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase text-slate-500">Part</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase text-slate-500">Grade</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase text-slate-500">{compareData.rev_old}</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase text-slate-500">{compareData.rev_new}</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase text-slate-500">Delta</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase text-slate-500">RM Old</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase text-slate-500">RM New</th>
                    <th className="px-4 py-2.5 text-center text-xs font-semibold uppercase text-slate-500">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredItems.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-12 text-center text-slate-400 text-sm">
                        No parts match the selected filter
                      </td>
                    </tr>
                  ) : filteredItems.map((item, i) => (
                    <tr key={i} className="hover:bg-slate-50 transition-colors" data-testid={`compare-row-${item.part_number}`}>
                      <td className="px-4 py-2.5">
                        <p className="font-mono text-xs font-semibold text-slate-800">{item.part_number}</p>
                        <p className="text-xs text-slate-500 truncate max-w-[180px]">{item.part_name}</p>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-slate-600">{item.material_grade || '—'}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-xs text-slate-500">{fmtCur(item.old_price)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-xs font-semibold text-slate-800">{fmtCur(item.new_price)}</td>
                      <td className="px-4 py-2.5 text-right">
                        <DeltaCell delta={item.price_delta} pct={item.price_delta_pct} />
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-xs text-slate-500">{fmtCur(item.old_rm_rate)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-xs text-slate-700">{fmtCur(item.new_rm_rate)}</td>
                      <td className="px-4 py-2.5 text-center">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${STATUS_STYLE[item.status] || STATUS_STYLE.unchanged}`}>
                          {item.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Empty state */}
      {!compareData && !comparing && (
        <div className="bg-white border border-slate-200 rounded-sm p-16 text-center shadow-sm">
          <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <GitCompare size={24} className="text-orange-500" />
          </div>
          <h3 className="font-heading font-bold text-slate-900 mb-2">Select revisions to compare</h3>
          <p className="text-slate-500 text-sm max-w-md mx-auto">
            Choose a customer and two revisions above to see a side-by-side comparison of part pricing, RM rates, and changes.
          </p>
        </div>
      )}
    </div>
  );
}
